import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, Save } from "lucide-react";

export default function RolesPermissions() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/settings/permissions").then((r) => {
      setData(r.data);
      setRoles(r.data.roles);
    });
  useEffect(() => { load(); }, []);

  if (!data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const toggle = (role, perm) => {
    setRoles((prev) => {
      const cur = new Set(prev[role] || []);
      cur.has(perm) ? cur.delete(perm) : cur.add(perm);
      return { ...prev, [role]: Array.from(cur) };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/permissions", { roles });
      toast.success(t("permissions.saved"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black text-slate-900">
            <ShieldCheck className="h-7 w-7 text-blue-600" /> {t("permissions.title")}
          </h1>
          <p className="mt-1 text-slate-500">{t("permissions.subtitle")}</p>
        </div>
        <Button data-testid="save-permissions" onClick={save} disabled={busy} className="gap-2 bg-blue-600 hover:bg-blue-500">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("permissions.save")}
        </Button>
      </div>

      <Card className="overflow-x-auto border-slate-200 bg-white p-0 shadow-sm">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 p-4 text-left font-heading font-bold text-slate-900">{t("permissions.permission")}</th>
              {data.editable_roles.map((role) => (
                <th key={role} className="p-4 text-center font-heading font-bold text-slate-700">
                  {t(`roles.${role}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.permissions.map((p) => (
              <tr key={p.key} data-testid={`perm-row-${p.key}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="sticky left-0 z-10 bg-white p-4 font-medium text-slate-800">{p.label}</td>
                {data.editable_roles.map((role) => {
                  const on = (roles[role] || []).includes(p.key);
                  return (
                    <td key={role} className="p-4 text-center">
                      <Checkbox
                        data-testid={`perm-${role}-${p.key}`}
                        checked={on}
                        onCheckedChange={() => toggle(role, p.key)}
                        className="mx-auto"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-slate-500">{t("permissions.superAdminNote")}</p>
    </div>
  );
}
